// src/components/TicketList.tsx
import React, { useEffect, useMemo, useState } from "react";
import { TicketModel } from "../../types/index";
import DataGrid, { Column, Scrolling } from "devextreme-react/data-grid";
import { useTranslation } from "react-i18next";
import { getSupportSocket } from "../../api/support-socket";
import { listTickets } from "../../api/support";

const STATUS_I18N_KEY: Record<number, string> = {
    0: "ticketStatusNew",
    1: "ticketStatusAnswered",
    2: "ticketStatusCustomerAnswered",
    3: "ticketStatusWaiting",
    4: "ticketStatusClosed",
    5: "ticketStatusUnknown",
};

const STATUS_STYLE: Record<number, string> = {
    0: "support-status-new",
    1: "support-status-answered",
    2: "support-status-customer",
    3: "support-status-waiting",
    4: "support-status-closed",
    5: "support-status-unknown",
};

type Props = {
    vendorId: string;
    selectedId?: string;
    onSelect: (ticket: TicketModel) => void;
    onCountChange?: (count: number) => void;
};

function formatListTimestamp(ts: any) {
    try {
        if (!ts) return "";
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        if (Number.isNaN(d.getTime())) return "";
        const now = new Date();
        const isToday =
            d.getDate() === now.getDate() &&
            d.getMonth() === now.getMonth() &&
            d.getFullYear() === now.getFullYear();
        return isToday
            ? d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
            : d.toLocaleDateString("tr-TR");
    } catch {
        return "";
    }
}

function stripHtml(html: string) {
    return html
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function getInitials(title?: string) {
    const value = (title ?? "").trim();
    if (!value) return "DT";
    const parts = value.split(/\s+/g).filter(Boolean);
    const first = parts[0]?.[0] ?? "D";
    const second = parts[1]?.[0] ?? parts[0]?.[1] ?? "T";
    return (first + second).toUpperCase();
}

export const TicketList: React.FC<Props> = ({ vendorId, selectedId, onSelect, onCountChange }) => {
    const [tickets, setTickets] = useState<TicketModel[]>([]);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState<number | "all">("all");
    const { t } = useTranslation();

    useEffect(() => {
        let cancelled = false;
        setTickets([]);
        onCountChange?.(0);
        (async () => {
            try {
                const data = await listTickets({ vendor_ref: Number(vendorId) || undefined });
                if (cancelled) return;
                const arr: any[] = Array.isArray(data) ? data : (data?.records ?? data?.items ?? data?.data ?? []);
                const list: TicketModel[] = arr
                    .map((raw: any) => {
                        const id = raw.id ?? raw.ref ?? raw.ticket_code;
                        if (!id) return null;
                        return { ...raw, id: String(id), createdAt: raw.createdAt ?? raw.created_at };
                    })
                    .filter(Boolean) as TicketModel[];
                setTickets(list);
                onCountChange?.(list.length);
            } catch (err) {
                console.warn("[TicketList] listTickets failed:", err);
            }
        })();
        return () => { cancelled = true; };
    }, [vendorId, onCountChange]);

    useEffect(() => {
        const socket = getSupportSocket();

        const normalize = (raw: any): TicketModel | undefined => {
            if (!raw) return undefined;
            const id = raw.id ?? raw.ref ?? raw.ticket_code;
            if (!id) return undefined;
            return {
                ...raw,
                id: String(id),
                createdAt: raw.createdAt ?? raw.created_at,
            };
        };

        const localCreatedHandler = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            const incoming = normalize(detail);
            if (!incoming) return;
            setTickets((prev) => {
                if (prev.some((x) => x.id === incoming.id)) return prev;
                const next = [incoming, ...prev];
                onCountChange?.(next.length);
                return next;
            });
        };
        window.addEventListener("support:ticket-created", localCreatedHandler);

        const handler = (msg: any) => {
            console.log("[TicketList] audit.log", msg);
            const payload = msg?.payload ?? msg;
            const evt = payload?.event ?? msg?.event;
            const incoming = normalize(payload?.ticket ?? msg?.ticket ?? payload);
            if (!incoming) return;

            setTickets((prev) => {
                let next = prev;
                if (evt === "TICKET_CREATED" || !evt) {
                    if (!prev.some((x) => x.id === incoming.id)) {
                        next = [incoming, ...prev];
                    }
                } else if (evt === "TICKET_UPDATED" || evt === "TICKET_CLOSED") {
                    const idx = prev.findIndex((x) => x.id === incoming.id);
                    if (idx >= 0) {
                        next = [...prev];
                        next[idx] = { ...prev[idx], ...incoming };
                    } else {
                        next = [incoming, ...prev];
                    }
                }
                onCountChange?.(next.length);
                return next;
            });
        };

        socket.on("queue.ticket.audit.log", handler);
        socket.on("queue.ticket.assignment", handler);
        return () => {
            socket.off("queue.ticket.audit.log", handler);
            socket.off("queue.ticket.assignment", handler);
            window.removeEventListener("support:ticket-created", localCreatedHandler);
        };
    }, [onCountChange]);


    const onRowClick = ({ data }) => {
        onSelect(data);
    };

    const filteredTickets = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        const statusFiltered =
            statusFilter === "all"
                ? tickets
                : tickets.filter((ticket) => (ticket.status ?? 5) === statusFilter);

        if (!q) return statusFiltered;

        return statusFiltered.filter((ticket) => {
            const haystack = [
                ticket.title ?? "",
                ticket.topic ?? "",
                ticket.createdBy ?? "",
                ticket.description ? stripHtml(ticket.description) : "",
            ]
                .join(" ")
                .toLowerCase();
            return haystack.includes(q);
        });
    }, [tickets, searchText, statusFilter]);

    const renderTicketCell = ({ data }: { data: TicketModel }) => {
        const isSelected = data.id === selectedId;
        const status = data.status ?? 5;
        const statusText = t(STATUS_I18N_KEY[status] ?? STATUS_I18N_KEY[5]);
        const statusStyle = STATUS_STYLE[status] ?? STATUS_STYLE[5];
        const title = data.title ?? "-";

        return (
            <div className="px-1 py-0.5">
                <div
                    className={[
                        "w-full rounded-xl px-3 py-3 border support-ticket-card support-surface support-border",
                        isSelected ? "support-selected" : "",
                    ].join(" ")}
                >
                    <div className="flex items-center gap-3">
                        <div
                            className="h-10 w-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                            style={{ background: 'var(--accent-color)', color: '#fff', letterSpacing: '0.03em' }}
                        >
                            {getInitials(title)}
                        </div>

                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <div className="font-semibold text-sm truncate">{title}</div>
                                <div className="ml-auto text-[11px] support-text-muted shrink-0">
                                    {formatListTimestamp(data.createdAt)}
                                </div>
                            </div>

                            <div className="mt-1 flex items-center gap-2">
                                <span className="text-[11px] support-text-muted truncate">
                                    {data.topic ?? t("general")}
                                </span>
                                <span
                                    className={[
                                        "text-[11px] px-2 py-0.5 rounded-full border support-badge support-status",
                                        statusStyle,
                                    ].join(" ")}
                                >
                                    {statusText}
                                </span>
                                {!!data.attachments?.length && (
                                    <span className="text-[11px] support-text-muted">
                                        {t("attachments")}: {data.attachments.length}
                                    </span>
                                )}
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="h-full min-h-0 flex flex-col">
            <div className="px-2 pb-2 flex items-center gap-2">
                <input
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder={t("search")}
                    className="flex-1 rounded-md border px-3 py-2 text-sm outline-none support-input support-border"
                />

                <select
                    value={String(statusFilter)}
                    onChange={(e) => {
                        const v = e.target.value;
                        setStatusFilter(v === "all" ? "all" : Number(v));
                    }}
                    className="w-[160px] rounded-md border px-2 py-2 text-sm outline-none support-input support-border"
                    aria-label={t("status")}
                    title={t("status")}
                >
                    <option value="all">{t("all")}</option>
                    <option value="0">{t("ticketStatusNew")}</option>
                    <option value="1">{t("ticketStatusAnswered")}</option>
                    <option value="2">{t("ticketStatusCustomerAnswered")}</option>
                    <option value="3">{t("ticketStatusWaiting")}</option>
                    <option value="4">{t("ticketStatusClosed")}</option>
                    <option value="5">{t("ticketStatusUnknown")}</option>
                </select>
            </div>

            <DataGrid
                className="support-ticket-grid"
                dataSource={filteredTickets}
                onRowClick={onRowClick}
                height={"100%"}
                keyExpr="id"
                showColumnHeaders={false}
                showBorders={false}
                showColumnLines={false}
                showRowLines={false}
                hoverStateEnabled
                rowAlternationEnabled={false}
                noDataText={t("noData")}
            >
                <Scrolling mode='virtual' />

                <Column
                    dataField="title"
                    allowSorting={false}
                    allowFiltering={false}
                    cellRender={renderTicketCell as any}
                />

            </DataGrid>
        </div>
    );
};
